import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Wand2,
  Upload,
  Download,
  Type,
  Palette,
  Layout,
  Save,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import { useStore } from '../store';
import { imageApi } from '../services/imageApi';
import { ImageGenerationModal } from './ImageGenerationModal';
import type { CoverDesign } from '../types/images';

interface CoverDesignerProps {
  isOpen: boolean;
  onClose: () => void;
}

const LAYOUTS: { id: CoverDesign['layout']; name: string }[] = [
  { id: 'centered', name: 'Centered' },
  { id: 'top', name: 'Top Aligned' },
  { id: 'bottom', name: 'Bottom Aligned' },
  { id: 'full-bleed', name: 'Full Bleed' },
];

// Google Fonts organized by category
const GOOGLE_FONTS = {
  'Serif': [
    'Playfair Display', 'Merriweather', 'Lora', 'Crimson Text', 'Libre Baskerville',
    'Cormorant Garamond', 'EB Garamond', 'Spectral', 'Bitter', 'Noto Serif',
    'Source Serif Pro', 'Vollkorn', 'Cardo', 'Alegreya', 'Libre Caslon Text'
  ],
  'Sans-Serif': [
    'Montserrat', 'Open Sans', 'Roboto', 'Lato', 'Poppins', 'Raleway',
    'Oswald', 'Source Sans Pro', 'Nunito', 'Work Sans', 'Quicksand',
    'Rubik', 'Inter', 'Manrope', 'DM Sans', 'Space Grotesk'
  ],
  'Display': [
    'Abril Fatface', 'Bebas Neue', 'Righteous', 'Alfa Slab One', 'Archivo Black',
    'Anton', 'Bowlby One SC', 'Bungee', 'Concert One', 'Fredoka One',
    'Lobster', 'Pacifico', 'Permanent Marker', 'Russo One', 'Secular One'
  ],
  'Handwriting': [
    'Dancing Script', 'Great Vibes', 'Parisienne', 'Sacramento', 'Alex Brush',
    'Allura', 'Cookie', 'Kaushan Script', 'Satisfy', 'Tangerine',
    'Yellowtail', 'Pinyon Script', 'Rouge Script', 'Marck Script', 'Niconne'
  ],
  'System': [
    'Georgia', 'Times New Roman', 'Garamond', 'Helvetica', 'Arial',
    'Futura', 'Didot', 'Baskerville', 'Palatino', 'Book Antiqua'
  ]
};

// Load Google Font dynamically
const loadGoogleFont = (fontName: string) => {
  if (GOOGLE_FONTS['System'].includes(fontName)) return;

  const fontId = `google-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(fontId)) return;

  const link = document.createElement('link');
  link.id = fontId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;700&display=swap`;
  document.head.appendChild(link);
};

export function CoverDesigner({ isOpen, onClose }: CoverDesignerProps) {
  const { coverDesign, setCoverDesign, currentSessionName, addNotification, user } = useStore();

  const [design, setDesign] = useState<Partial<CoverDesign>>({
    title: currentSessionName || 'My Book',
    subtitle: '',
    authorName: user?.name || 'Author Name',
    imageUrl: '',
    layout: 'centered',
    titleFont: 'Georgia',
    titleColor: '#FFFFFF',
    subtitleColor: '#E0E0E0',
    authorColor: '#FFFFFF',
    backgroundColor: '#1a1a2e',
    overlayOpacity: 0.3,
  });

  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [customFonts, setCustomFonts] = useState<string[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fontInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (coverDesign) {
      setDesign(coverDesign);
    }
  }, [coverDesign]);

  // Load Google Font when font changes
  useEffect(() => {
    if (design.titleFont) {
      loadGoogleFont(design.titleFont);
    }
  }, [design.titleFont]);

  useEffect(() => {
    renderPreview();
  }, [design]);

  const renderPreview = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = 600;
    const height = 900;
    canvas.width = width;
    canvas.height = height;

    // Background
    ctx.fillStyle = design.backgroundColor || '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    // Background image if available
    if (design.imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Draw image to cover
        const scale = Math.max(width / img.width, height / img.height);
        const x = (width - img.width * scale) / 2;
        const y = (height - img.height * scale) / 2;
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

        // Overlay
        if (design.overlayOpacity && design.overlayOpacity > 0) {
          ctx.fillStyle = `rgba(0, 0, 0, ${design.overlayOpacity})`;
          ctx.fillRect(0, 0, width, height);
        }

        // Draw text after image loads
        drawText(ctx, width, height);
      };
      img.src = design.imageUrl;
    } else {
      drawText(ctx, width, height);
    }
  };

  const drawText = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const layout = design.layout || 'centered';
    const titleSize = 48;
    const subtitleSize = 24;
    const authorSize = 20;

    // Calculate positions based on layout
    let titleY: number;
    let subtitleY: number;
    let authorY: number;

    switch (layout) {
      case 'top':
        titleY = 150;
        subtitleY = titleY + titleSize + 20;
        authorY = subtitleY + subtitleSize + 30;
        break;
      case 'bottom':
        authorY = height - 100;
        subtitleY = authorY - authorSize - 30;
        titleY = subtitleY - subtitleSize - 20;
        break;
      case 'full-bleed':
        titleY = height / 2 - 50;
        subtitleY = titleY + titleSize + 15;
        authorY = height - 80;
        break;
      default: // centered
        titleY = height / 2 - 40;
        subtitleY = titleY + titleSize + 15;
        authorY = subtitleY + subtitleSize + 40;
    }

    ctx.textAlign = 'center';

    // Title
    ctx.font = `bold ${titleSize}px ${design.titleFont || 'Georgia'}`;
    ctx.fillStyle = design.titleColor || '#FFFFFF';
    wrapText(ctx, design.title || '', width / 2, titleY, width - 80, titleSize + 10);

    // Subtitle
    if (design.subtitle) {
      ctx.font = `italic ${subtitleSize}px ${design.titleFont || 'Georgia'}`;
      ctx.fillStyle = design.subtitleColor || '#E0E0E0';
      ctx.fillText(design.subtitle, width / 2, subtitleY);
    }

    // Author
    ctx.font = `${authorSize}px ${design.titleFont || 'Georgia'}`;
    ctx.fillStyle = design.authorColor || '#FFFFFF';
    ctx.fillText(design.authorName || '', width / 2, authorY);
  };

  const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
  ) => {
    const words = text.split(' ');
    let line = '';
    let currentY = y;

    for (const word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line) {
        ctx.fillText(line, x, currentY);
        line = word + ' ';
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, currentY);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setDesign((prev) => ({ ...prev, imageUrl: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFontUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fontName = file.name.replace(/\.(ttf|otf|woff|woff2)$/i, '');
      const reader = new FileReader();
      reader.onload = () => {
        const fontFace = new FontFace(fontName, `url(${reader.result})`);
        fontFace.load().then((loadedFont) => {
          document.fonts.add(loadedFont);
          setCustomFonts((prev) => [...prev, fontName]);
          setDesign((prev) => ({ ...prev, titleFont: fontName }));
          addNotification(`Font "${fontName}" loaded`, 'success');
        }).catch(() => {
          addNotification('Failed to load font', 'error');
        });
      };
      reader.readAsDataURL(file);
    }
    if (fontInputRef.current) {
      fontInputRef.current.value = '';
    }
  };

  const handleGeneratedImage = (url: string) => {
    setDesign((prev) => ({ ...prev, imageUrl: url }));
    setShowImageGenerator(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await imageApi.saveCoverDesign(design as Omit<CoverDesign, 'id'>);
      if (result.success) {
        setCoverDesign(result.cover);
        addNotification('Cover design saved', 'success');
      }
    } catch (err) {
      addNotification('Failed to save cover design', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `${design.title || 'cover'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent/20 rounded-lg">
                <ImageIcon className="w-5 h-5 text-accent" />
              </div>
              <h2 className="text-xl font-semibold">Cover Designer</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
              {/* Controls */}
              <div className="space-y-6">
                {/* Text Content */}
                <div className="space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    Text Content
                  </h3>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Title</label>
                    <input
                      type="text"
                      value={design.title || ''}
                      onChange={(e) =>
                        setDesign((prev) => ({ ...prev, title: e.target.value }))
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Book Title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={design.subtitle || ''}
                      onChange={(e) =>
                        setDesign((prev) => ({ ...prev, subtitle: e.target.value }))
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Optional subtitle"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Author</label>
                    <input
                      type="text"
                      value={design.authorName || ''}
                      onChange={(e) =>
                        setDesign((prev) => ({ ...prev, authorName: e.target.value }))
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Author Name"
                    />
                  </div>
                </div>

                {/* Background Image */}
                <div className="space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Background Image
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowImageGenerator(true)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg hover:border-accent hover:bg-accent/10 transition-colors"
                    >
                      <Wand2 className="w-5 h-5 text-accent" />
                      <span>Generate with AI</span>
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg hover:border-gray-400 transition-colors"
                    >
                      <Upload className="w-5 h-5 text-gray-500" />
                      <span>Upload Image</span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                  {design.imageUrl && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-green-600">✓ Image set</span>
                      <button
                        onClick={() => setDesign((prev) => ({ ...prev, imageUrl: '' }))}
                        className="text-sm text-red-500 hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* Layout */}
                <div className="space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Layout className="w-4 h-4" />
                    Layout
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {LAYOUTS.map((layout) => (
                      <button
                        key={layout.id}
                        onClick={() =>
                          setDesign((prev) => ({ ...prev, layout: layout.id }))
                        }
                        className={`px-4 py-2 rounded-lg border transition-colors ${
                          design.layout === layout.id
                            ? 'border-accent bg-accent/10 text-accent-dark'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {layout.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Typography */}
                <div className="space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Typography & Colors
                  </h3>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Font</label>
                    <select
                      value={design.titleFont || 'Georgia'}
                      onChange={(e) => {
                        const font = e.target.value;
                        loadGoogleFont(font);
                        setDesign((prev) => ({ ...prev, titleFont: font }));
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      {customFonts.length > 0 && (
                        <optgroup label="Custom Fonts">
                          {customFonts.map((font) => (
                            <option key={font} value={font}>{font}</option>
                          ))}
                        </optgroup>
                      )}
                      {Object.entries(GOOGLE_FONTS).map(([category, fonts]) => (
                        <optgroup key={category} label={category}>
                          {fonts.map((font) => (
                            <option key={font} value={font}>{font}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <input
                      ref={fontInputRef}
                      type="file"
                      accept=".ttf,.otf,.woff,.woff2"
                      onChange={handleFontUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fontInputRef.current?.click()}
                      className="mt-2 text-xs text-accent hover:text-accent-dark"
                    >
                      + Upload custom font
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Title Color</label>
                      <input
                        type="color"
                        value={design.titleColor || '#FFFFFF'}
                        onChange={(e) =>
                          setDesign((prev) => ({ ...prev, titleColor: e.target.value }))
                        }
                        className="w-full h-10 rounded-lg cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Background Color
                      </label>
                      <input
                        type="color"
                        value={design.backgroundColor || '#1a1a2e'}
                        onChange={(e) =>
                          setDesign((prev) => ({
                            ...prev,
                            backgroundColor: e.target.value,
                          }))
                        }
                        className="w-full h-10 rounded-lg cursor-pointer"
                      />
                    </div>
                  </div>
                  {design.imageUrl && (
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Overlay Darkness: {Math.round((design.overlayOpacity || 0.3) * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="0.8"
                        step="0.05"
                        value={design.overlayOpacity || 0.3}
                        onChange={(e) =>
                          setDesign((prev) => ({
                            ...prev,
                            overlayOpacity: parseFloat(e.target.value),
                          }))
                        }
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Preview */}
              <div className="flex flex-col items-center">
                <div className="bg-gray-100 rounded-xl p-6 inline-block">
                  <canvas
                    ref={canvasRef}
                    className="max-h-[500px] rounded-lg shadow-lg"
                    style={{ width: '300px', height: '450px' }}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  Preview (actual size: 600x900px)
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-between">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-white"
            >
              <Download className="w-4 h-4" />
              Download PNG
            </button>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Cover
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Generator Modal */}
      <ImageGenerationModal
        isOpen={showImageGenerator}
        onClose={() => setShowImageGenerator(false)}
        onImageSelect={handleGeneratedImage}
        initialPrompt={`Book cover background for "${design.title}"`}
      />
    </>
  );
}

export default CoverDesigner;
